import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag, Plus, ShieldAlert, Check, Loader2, X, Sparkles, Store, User, Receipt } from 'lucide-react';
import { Card, Badge } from './AdminPanelPrimitives';
import { StatusChip, EmptyState } from './adminDesignSystem';
import { AdminAutocomplete } from './AdminAutocomplete';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  open: 'red',
  acknowledged: 'orange',
  resolved: 'green',
  closed: '',
};

const STATUS_NEXT = {
  open: 'acknowledged',
  acknowledged: 'resolved',
  resolved: 'closed',
  closed: null,
};

const STATUS_NEXT_LABEL = {
  open: 'Acknowledge',
  acknowledged: 'Mark resolved',
  resolved: 'Close',
};

const fmtMoney = (amount, currency) => {
  const num = Number(amount || 0);
  const cur = (currency || 'EUR').toUpperCase();
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(num); }
  catch { return `${num.toFixed(2)} ${cur}`; }
};

export const AdminDisputesPanel = ({ headers }) => {
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [pickedTxn, setPickedTxn] = useState(null);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const res = await axios.get(`${API}/admin/disputes`, { headers, params });
      setItems(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load disputes');
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter]);

  useEffect(() => { fetchDisputes(); }, [fetchDisputes]);

  const resetCreate = () => {
    setShowCreate(false);
    setPickedTxn(null);
    setReason('');
    setNotes('');
  };

  const handleCreate = async () => {
    if (!pickedTxn?.id) {
      toast.error('Pick a transaction first.');
      return;
    }
    if (reason.trim().length < 3) {
      toast.error('Reason must be at least 3 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/disputes`, {
        transaction_id: pickedTxn.id,
        reason: reason.trim(),
        notes: notes.trim(),
      }, { headers });
      toast.success('Dispute opened · venue owner notified');
      resetCreate();
      fetchDisputes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open dispute');
    } finally {
      setSubmitting(false);
    }
  };

  const advanceStatus = async (dispute) => {
    const nextStatus = STATUS_NEXT[dispute.status];
    if (!nextStatus) return;
    try {
      await axios.patch(`${API}/admin/disputes/${dispute.id}`, { status: nextStatus }, { headers });
      toast.success(`Dispute moved to ${nextStatus}`);
      fetchDisputes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update dispute');
    }
  };

  const reopenDispute = async (dispute) => {
    if (!window.confirm('Re-open this dispute?')) return;
    try {
      await axios.patch(`${API}/admin/disputes/${dispute.id}`, { status: 'open' }, { headers });
      toast.success('Dispute reopened');
      fetchDisputes();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen');
    }
  };

  const stats = useMemo(() => {
    const counts = { open: 0, acknowledged: 0, resolved: 0, closed: 0 };
    items.forEach((row) => { counts[row.status] = (counts[row.status] || 0) + 1; });
    return counts;
  }, [items]);

  const renderTxnRow = (row) => (
    <>
      <Receipt size={14} color="#5B7EFF" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>
          {row.venue_name || 'Unknown venue'} · {fmtMoney(row.amount || row.total_amount, row.currency)}
        </span>
        <span style={{ fontSize: 11, color: '#777' }}>
          {row.guest_name || row.guest_email || 'Unknown guest'} · {new Date(row.created_at || Date.now()).toLocaleString()} · {row.status || ''}
        </span>
      </div>
      <code style={{ fontSize: 10, color: '#999' }}>{row.id?.slice(0, 8)}…</code>
    </>
  );

  return (
    <div data-testid="admin-disputes-panel" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card style={{ padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 22 }}>Transaction Disputes</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>
              Track guest complaints. Money flows direct via Viva — Allure tracks the dispute lifecycle and keeps venues accountable. The venue owner is notified by email + push + in-app when a dispute is opened.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            data-testid="admin-dispute-open-new-button"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 14, border: 'none', background: '#111', color: '#FFF', cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
          >
            <Plus size={14} /> Open dispute
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
          {[
            { id: 'all', label: 'All', tone: 'neutral', count: items.length },
            { id: 'open', label: 'Open', tone: 'urgent', count: stats.open || 0 },
            { id: 'acknowledged', label: 'Acknowledged', tone: 'early', count: stats.acknowledged || 0 },
            { id: 'resolved', label: 'Resolved', tone: 'active', count: stats.resolved || 0 },
            { id: 'closed', label: 'Closed', tone: 'resolved', count: stats.closed || 0 },
          ].map((tab) => (
            <StatusChip
              key={tab.id}
              tone={tab.tone}
              label={tab.label}
              count={tab.count}
              active={statusFilter === tab.id}
              onClick={() => setStatusFilter(tab.id)}
              dataTestId={`admin-dispute-filter-${tab.id}`}
            />
          ))}
        </div>
      </Card>

      {showCreate ? (
        <Card style={{ padding: 18, borderColor: '#DD0608' }} data-testid="admin-dispute-create-form">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p style={{ margin: 0, fontWeight: 700, fontFamily: "'Inria Serif', serif", fontSize: 16 }}>Open new dispute</p>
            <button onClick={resetCreate} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
          </div>
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 6px' }}>Transaction</p>
              {pickedTxn ? (
                <div data-testid="admin-dispute-txn-chip" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: '#F4F5F8', border: '1px solid #E7E7E7' }}>
                  <Receipt size={14} color="#5B7EFF" />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111' }}>{pickedTxn.venue_name || 'Unknown venue'} · {fmtMoney(pickedTxn.amount || pickedTxn.total_amount, pickedTxn.currency)}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: '#777' }}>{pickedTxn.guest_name || pickedTxn.guest_email} · <code>{pickedTxn.id}</code></p>
                  </div>
                  <button onClick={() => setPickedTxn(null)} data-testid="admin-dispute-txn-clear" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={14} /></button>
                </div>
              ) : (
                <AdminAutocomplete
                  endpoint="/admin/directory/transactions"
                  headers={headers}
                  placeholder="Search by venue, guest email, or transaction ID…"
                  minChars={0}
                  renderRow={renderTxnRow}
                  onSelect={setPickedTxn}
                  emptyHint="No transactions yet. Start by exercising a guest QR scan to seed data."
                  dataTestId="admin-dispute-txn-autocomplete"
                />
              )}
            </div>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Reason (visible to venue)
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="admin-dispute-reason-input"
                placeholder="Short reason — e.g. Guest reports wrong amount charged"
                style={{ display: 'block', width: '100%', marginTop: 6, padding: '10px 12px', border: '1px solid #E7E7E7', borderRadius: 12, fontSize: 14, fontWeight: 500 }}
              />
            </label>
            <label style={{ fontSize: 11, fontWeight: 700, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Internal notes (optional · admin-only)
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="admin-dispute-notes-input"
                rows={3}
                placeholder="Context only admins will see"
                style={{ display: 'block', width: '100%', marginTop: 6, padding: '10px 12px', border: '1px solid #E7E7E7', borderRadius: 12, fontSize: 13.5, resize: 'vertical' }}
              />
            </label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button
                type="button"
                onClick={handleCreate}
                disabled={submitting || !pickedTxn}
                data-testid="admin-dispute-submit-button"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 12, border: 'none', background: pickedTxn ? '#DD0608' : '#9aa0a6', color: '#FFF', cursor: submitting ? 'wait' : 'pointer', fontWeight: 700 }}
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />} Open & notify venue
              </button>
              <button type="button" onClick={resetCreate} style={{ padding: '10px 16px', borderRadius: 12, border: '1px solid #E7E7E7', background: '#FFF', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </Card>
      ) : null}

      <div data-testid="admin-disputes-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? <p style={{ textAlign: 'center', color: '#999' }}>Loading disputes…</p> : null}
        {!loading && items.length === 0 ? (
          statusFilter === 'all' ? (
            <EmptyState
              icon={Sparkles}
              tone="active"
              title="No disputes — all transactions are healthy"
              body="Disputes appear here when a guest reports a transaction issue. Open one manually for any paid transaction (the venue owner gets notified by email + push + in-app)."
              actionLabel="Open dispute manually"
              onAction={() => setShowCreate(true)}
              dataTestId="admin-disputes-empty-state"
            />
          ) : (
            <EmptyState
              icon={ShieldAlert}
              tone="neutral"
              title={`Nothing in "${statusFilter}"`}
              body={`No disputes are currently in the "${statusFilter}" state. Switch filters or open a new dispute.`}
              actionLabel="See all disputes"
              onAction={() => setStatusFilter('all')}
              dataTestId="admin-disputes-empty-state"
            />
          )
        ) : null}
        {items.map((row) => {
          const isExpanded = expandedId === row.id;
          return (
            <Card key={row.id} data-testid={`admin-dispute-row-${row.id}`} style={{ padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 15, fontWeight: 700 }}>
                    <Flag size={14} style={{ marginRight: 6, verticalAlign: -2 }} color="#DD0608" />
                    {row.venue_name || 'Unknown venue'} · {fmtMoney(row.amount, row.currency)}
                  </p>
                  <p style={{ margin: '4px 0', fontSize: 13, color: '#555' }}>{row.reason}</p>
                  <p style={{ margin: 0, fontSize: 11, color: '#888' }}>
                    <User size={10} style={{ verticalAlign: -1, marginRight: 3 }} />
                    {row.guest_name || row.guest_email || 'Unknown guest'}
                    {' · '}Txn <code>{row.transaction_id}</code>
                    {' · '}opened {new Date(row.created_at).toLocaleString()}
                  </p>
                  {row.notes ? <p style={{ margin: '6px 0 0', fontSize: 12, color: '#888', fontStyle: 'italic' }}>"{row.notes}"</p> : null}
                  {row.owner_email ? (
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: '#5B7EFF', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Store size={10} /> Venue owner notified: {row.owner_email}
                    </p>
                  ) : (
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: '#DD0608' }}>
                      ⚠ No venue owner resolved — notification was not sent.
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                  <Badge text={row.status} color={STATUS_COLORS[row.status]} />
                  {STATUS_NEXT[row.status] ? (
                    <button
                      type="button"
                      onClick={() => advanceStatus(row)}
                      data-testid={`admin-dispute-advance-${row.id}`}
                      style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #00C853', background: '#E8F9EF', color: '#00A246', cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                      <Check size={12} /> {STATUS_NEXT_LABEL[row.status]}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => reopenDispute(row)}
                      data-testid={`admin-dispute-reopen-${row.id}`}
                      style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #DD0608', background: '#FFF', color: '#DD0608', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}
                    >
                      Reopen
                    </button>
                  )}
                </div>
              </div>
              {(row.history || []).length > 1 ? (
                <button
                  type="button"
                  onClick={() => setExpandedId(isExpanded ? null : row.id)}
                  data-testid={`admin-dispute-history-toggle-${row.id}`}
                  style={{ marginTop: 10, fontSize: 11, color: '#666', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                >
                  {isExpanded ? 'Hide history' : `Show history (${row.history.length})`}
                </button>
              ) : null}
              {isExpanded && (row.history || []).length > 0 ? (
                <ul style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 11, color: '#666' }}>
                  {row.history.map((h, i) => (
                    <li key={i}>
                      {new Date(h.ts).toLocaleString()} — {h.action}
                      {h.to_status ? ` → ${h.to_status}` : ''}
                      {h.actor_email ? ` (${h.actor_email})` : ''}
                      {h.note ? ` · "${h.note}"` : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default AdminDisputesPanel;
